import React, { SetStateAction, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { ChildQuestionItemComponent } from "./ChildQuestionItemComponent";
import { ChildQuestionItemButton } from "./ChildQuestionItemButton";
import { BorderStyle, DeveloperErrorFlow } from "@pulse/shared-components";
import { QuestionItemProps } from "./QuestionItem";
import { Instance } from "mobx-state-tree";
import { TFunction } from "i18next";
import { SurveyStore } from "../../store/SurveyStore";
import {
  FoundationColorTokens,
  Typography as LeoTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { QuestionDetailsStore } from "../../store/QuestionDetailsStore";

export interface ChildQuestionItemsListProps {
  questionItem: QuestionItemProps;
  questionDetails: Instance<typeof QuestionDetailsStore>;
  t: TFunction;
  surveyStore: Instance<typeof SurveyStore>;
  setSelectedItemQuestionId: (value: string | null) => void;
  setIsMessageBoxVisible: (value: SetStateAction<boolean>) => void;
  setMessageBoxQuestionItemId: (value: SetStateAction<string | null>) => void;
  tokens: FoundationColorTokens<string>;
  typography: LeoTypography;
  selectedQuestionItemId: string | null;
  border: BorderStyle;
  childQuestionIds: string[];
}

export const ChildQuestionItemsList = observer(
  ({
    questionDetails,
    questionItem,
    t,
    surveyStore,
    setIsMessageBoxVisible,
    setMessageBoxQuestionItemId,
    setSelectedItemQuestionId,
    selectedQuestionItemId,
    childQuestionIds,
    ...props
  }: ChildQuestionItemsListProps): React.ReactElement => {
    const childRefs = useRef<(HTMLDivElement | null)[]>([]);
    const newChildQuestion = surveyStore.findNewlyAddedGroupChildQuestion(
      questionItem.questionId,
    );

    useEffect(() => {
      const selectedChildIndex = childQuestionIds.findIndex(
        (id) =>
          surveyStore.findQuestionById(id)?.surveyQuestionDetails.questionId ===
          selectedQuestionItemId,
      );
      if (selectedChildIndex !== -1 && childRefs.current[selectedChildIndex]) {
        childRefs.current[selectedChildIndex]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, [selectedQuestionItemId, childQuestionIds]);

    return (
      <>
        {childQuestionIds.map((childQuestionId, index) => {
          const childQuestionDetails =
            surveyStore.findQuestionById(childQuestionId);
          const isItemSelected =
            selectedQuestionItemId ===
            childQuestionDetails?.surveyQuestionDetails.questionId;

          return (
            <div
              key={childQuestionId}
              ref={(el) => (childRefs.current[index] = el)}
            >
              {childQuestionDetails ? (
                <div>
                  {!isItemSelected ? (
                    <ChildQuestionItemButton
                      {...props}
                      childQuestionDetails={childQuestionDetails}
                      questionDetails={questionDetails}
                      questionId={questionItem.questionId}
                      setSelectedItemQuestionId={setSelectedItemQuestionId}
                      setIsMessageBoxVisible={setIsMessageBoxVisible}
                      setMessageBoxQuestionItemId={setMessageBoxQuestionItemId}
                      t={t}
                    >
                      <ChildQuestionItemComponent
                        {...props}
                        childQuestionDetails={childQuestionDetails}
                        isItemSelected={isItemSelected}
                      />
                    </ChildQuestionItemButton>
                  ) : (
                    <ChildQuestionItemComponent
                      {...props}
                      childQuestionDetails={childQuestionDetails}
                      isItemSelected={isItemSelected}
                    />
                  )}
                </div>
              ) : (
                <DeveloperErrorFlow />
              )}
            </div>
          );
        })}

        {newChildQuestion && (
          <ChildQuestionItemComponent
            {...props}
            childQuestionDetails={newChildQuestion}
            isItemSelected={true}
          />
        )}
      </>
    );
  },
);
